//devices.ts — camera enumeration and explicit device selection

export interface CameraDevice {
	readonly deviceId: string
	readonly label: string
}

export class CameraError extends Error {
	constructor(
		message: string,
		public readonly kind: 'denied' | 'notFound' | 'inUse' | 'unsupported' | 'unknown',
	) {
		super(message)
		this.name = 'CameraError'
	}
}

//Labels are empty until a stream has been granted once, so callers should list devices
//after the first successful getUserMedia rather than before.
export async function listCameras(): Promise<CameraDevice[]> {
	if (!navigator.mediaDevices?.enumerateDevices) return []
	const devices = await navigator.mediaDevices.enumerateDevices()
	return devices
		.filter((device) => device.kind === 'videoinput')
		.map((device, index) => ({
			deviceId: device.deviceId,
			label: device.label || `Camera ${index + 1}`,
		}))
}

//720p is the VTON model's output cap, so capturing higher only wastes upload. The
//shape follows the screen: a portrait mirror asks for a portrait stream, and Lucy
//maps output to the same shape. `ideal` constraints mean a landscape-only webcam
//still opens fine — the browser just serves the closest it has.
//swapOrientation flips the request for a physically tilted camera: asking a sideways
//camera for the screen's shape makes Chrome centre-crop the sensor (the "zoomed in"
//failure) — ask for the sensor's natural shape instead and let the rotation pass
//produce the screen's shape at full frame.
const targetSize = (swapOrientation: boolean): { width: number; height: number } => {
	const wantPortrait = (window.innerHeight > window.innerWidth) !== swapOrientation
	return wantPortrait ? { width: 720, height: 1280 } : { width: 1280, height: 720 }
}

export interface OpenCameraOptions {
	readonly deviceId: string | null
	//True when captureRotation is an odd quarter turn.
	readonly swapOrientation: boolean
}

export async function openCamera(options: OpenCameraOptions): Promise<MediaStream> {
	if (!navigator.mediaDevices?.getUserMedia) {
		throw new CameraError(
			'This browser cannot open a camera. A secure context (https or localhost) is required.',
			'unsupported',
		)
	}

	const size = targetSize(options.swapOrientation)
	//An exact deviceId is the whole point: without it the browser picks, and on a Mac that
	//can mean grabbing the iPhone Continuity Camera instead of the mirror's own camera.
	const video: MediaTrackConstraints = {
		width: { ideal: size.width },
		height: { ideal: size.height },
		frameRate: { ideal: 30 },
		...(options.deviceId ? { deviceId: { exact: options.deviceId } } : {}),
	}

	try {
		return await navigator.mediaDevices.getUserMedia({ video, audio: false })
	} catch (cause) {
		//A pinned camera that has been unplugged fails with OverconstrainedError. Falling
		//back beats leaving the mirror dark, but only when a specific device was requested.
		if (options.deviceId && isOverconstrained(cause)) {
			return openCamera({ ...options, deviceId: null })
		}
		throw asCameraError(cause)
	}
}

export function stopStream(stream: MediaStream | null): void {
	stream?.getTracks().forEach((track) => track.stop())
}

const isOverconstrained = (cause: unknown): boolean =>
	cause instanceof DOMException &&
	(cause.name === 'OverconstrainedError' || cause.name === 'NotFoundError')

function asCameraError(cause: unknown): CameraError {
	if (!(cause instanceof DOMException)) {
		return new CameraError(`Could not open the camera: ${String(cause)}`, 'unknown')
	}
	switch (cause.name) {
		case 'NotAllowedError':
		case 'SecurityError':
			return new CameraError(
				'Camera access was blocked. Allow it for this site and reload.',
				'denied',
			)
		case 'NotFoundError':
		case 'OverconstrainedError':
			return new CameraError('No camera matched. Pick a different source in Video settings.', 'notFound')
		case 'NotReadableError':
		case 'AbortError':
			return new CameraError('The camera is already in use by another app.', 'inUse')
		default:
			return new CameraError(`Could not open the camera: ${cause.message}`, 'unknown')
	}
}
